import { useState } from 'react';
import NewsCard from './NewsCard';
import ArticleModal from './ArticleModal';
import InterstitialAd from './InterstitialAd';
import ShareMenu from './ShareMenu';
import { CarouselDots } from './CarouselDots';
import { CarouselNavButtons } from './CarouselNavButtons';
import { useCarouselScroll } from '../hooks/useCarouselScroll';
import { useAuth } from '../context/AuthContext';
import { redirectToGoogleSignIn } from '../lib/googleAuth';
import { Share2, Bookmark, BookmarkCheck, Unlock } from 'lucide-react';

const StoryCarousel = ({ story }) => {
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [showInterstitial, setShowInterstitial] = useState(false);
  const [interstitialUrl, setInterstitialUrl] = useState('');
  const [showShare, setShowShare] = useState(false);
  const { isLoggedIn, selectedSources, hasConfiguredSources, bookmarkedIds, toggleBookmark, recordBubbleBreak } = useAuth();

  const articles = story.articles || [];
  const apiPickIndex = articles.findIndex((a) => a.is_clarity_pick);
  const clarityPickIndex = apiPickIndex >= 0 ? apiPickIndex : Math.floor(articles.length / 2);
  const outsideBubbleCount = hasConfiguredSources
    ? articles.filter((a) => !selectedSources.includes(a.source.id)).length
    : 0;
  const isBookmarked = bookmarkedIds.includes(story.id);

  const {
    scrollContainerRef, currentIndex, isMobile, hasOverflow,
    scrollToIndex, goToPrevious, goToNext, handleScroll,
  } = useCarouselScroll(articles, clarityPickIndex);

  const handleReadFullStory = (url) => {
    setInterstitialUrl(url);
    setSelectedArticle(null);
    setShowInterstitial(true);
  };

  const handleCloseInterstitial = () => {
    setShowInterstitial(false);
    setInterstitialUrl('');
  };

  const handleArticleClick = (article) => {
    setSelectedArticle(article);
    if (hasConfiguredSources && !selectedSources.includes(article.source.id)) {
      recordBubbleBreak(article.source.id);
    }
  };

  const handleBookmarkClick = () => {
    if (!isLoggedIn) {
      redirectToGoogleSignIn('/');
      return;
    }
    toggleBookmark(story.id);
  };

  if (articles.length === 0) return null;

  return (
    <div className="mb-8 sm:mb-12" data-testid="story-carousel">
      {/* Story Header */}
      <div className="mb-4 sm:mb-6 flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-serif font-bold text-gray-900 mb-1 sm:mb-2 leading-tight">
            {story.primary_headline}
          </h2>
          <div className="flex items-center gap-3 text-xs sm:text-sm text-gray-600">
            <span>{story.article_count} {story.article_count === 1 ? 'source' : 'sources'}</span>
            {!story.has_varying_stance && story.article_count >= 3 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">
                ⚠️ No varying stance detected
              </span>
            )}
          </div>
          {outsideBubbleCount > 0 && (
            <div
              className="mt-2 inline-flex items-center gap-1.5 text-xs sm:text-sm text-purple-700 bg-purple-50 border border-purple-100 rounded-full px-3 py-1"
              data-testid="outside-bubble-banner"
            >
              <Unlock className="w-3.5 h-3.5 flex-shrink-0" />
              <span>{outsideBubbleCount} of {articles.length} sources here are outside the bubble you usually read</span>
            </div>
          )}
        </div>
        {story.article_count >= 1 && story.id < 1000000 && (
          <div className="shrink-0 flex items-center gap-2">
            <button
              onClick={handleBookmarkClick}
              className={`flex items-center justify-center p-2 rounded-full border transition-colors ${
                isBookmarked ? 'border-indigo-500 text-indigo-600 bg-indigo-50' : 'border-gray-200 hover:border-indigo-500 hover:text-indigo-600 text-gray-700 bg-white'
              }`}
              data-testid="story-bookmark-button"
              aria-label={isBookmarked ? 'Remove bookmark' : 'Bookmark this story'}
            >
              {isBookmarked ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
            </button>
            <button
              onClick={() => setShowShare(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-full border border-gray-200 hover:border-blue-500 hover:text-blue-600 text-gray-700 text-xs sm:text-sm font-medium transition-colors bg-white"
              data-testid="story-share-button"
              aria-label="Share this story"
            >
              <Share2 className="w-4 h-4" />
              <span className="hidden sm:inline">Share</span>
              {story.share_count > 0 && <span className="text-gray-400">· {story.share_count}</span>}
            </button>
          </div>
        )}
      </div>

      {/* Carousel */}
      <div className="relative">
        {articles.length > 1 && !isMobile && hasOverflow && (
          <CarouselNavButtons onPrevious={goToPrevious} onNext={goToNext} />
        )}

        {/* Cards Container - Swipeable on mobile */}
        <div
          ref={scrollContainerRef}
          className="flex gap-3 sm:gap-4 overflow-x-auto scrollbar-hide snap-x snap-mandatory scroll-smooth"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          onScroll={handleScroll}
        >
          {articles.map((article, index) => (
            <div
              key={article.id}
              data-card
              data-testid={`article-card-${article.id}`}
              className="flex-shrink-0 snap-center w-full sm:w-[calc(50%-0.5rem)] lg:w-[calc(33.333%-0.667rem)]"
            >
              <NewsCard
                article={article}
                isClarityPick={index === clarityPickIndex}
                isOutsideBubble={hasConfiguredSources && !selectedSources.includes(article.source.id)}
                onClick={() => handleArticleClick(article)}
              />
            </div>
          ))}
        </div>

        <CarouselDots articles={articles} currentIndex={currentIndex} onDotClick={scrollToIndex} />
      </div>

      {/* Article Modal */}
      {selectedArticle && (
        <ArticleModal
          article={selectedArticle}
          isOpen={!!selectedArticle}
          onClose={() => setSelectedArticle(null)}
          onReadFullStory={handleReadFullStory}
        />
      )}

      {/* Interstitial Ad */}
      {showInterstitial && (
        <InterstitialAd
          targetUrl={interstitialUrl}
          onClose={handleCloseInterstitial}
          autoRedirectDelay={5}
        />
      )}

      {/* Share Menu */}
      {showShare && (
        <ShareMenu story={story} onClose={() => setShowShare(false)} />
      )}
    </div>
  );
};

export default StoryCarousel;
